import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { getFirstPartOfURLPath } from 'utils/helpers'
import { ReactComponent as DropdownIcon } from 'images/common/dropdown.svg'
import { ReactComponent as SearchIcon } from 'images/stations-selector/search.svg'
import { useStations } from 'components/common/hooks/useStations'
import { amplitudeEvent, UserEvents } from 'config/amplitude'
import classNames from 'classnames/bind'
import styles from './StationSelector.module.scss'
import Tooltip from 'react-bootstrap/Tooltip'
import Overlay from 'react-bootstrap/Overlay'
import { groupByVar } from 'config/apollo/analytics-report/variables'
import { calculateSelectedStationsAndGroupsAmount, getGroupData } from '../../utils'
import { useStationGroups } from 'components/analytics/context/StationGroupsContext'
import StationList from './components/StationList'
import GroupList from './components/GroupList'


const cx = classNames.bind(styles)


const StationSelector = ({
  value,
  customClass,
  leftAccessory,
  placeholder,
  allStationsText,
  maxHeight,
  onValueChange,
  disabled,
  selectButtons,
  itemsLimit,
  allowGroups
}) => {
  const { t } = useTranslation()
  const { stations } = useStations()
  const { groupsLoading } = useStationGroups()

  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)
  const [tooltipTarget, setTooltipTarget] = useState(null)

  const { groups } = useStationGroups()
  const [showGroups, setShowGroups] = useState(false)

  useEffect(() => {
    if (!itemsLimit) {
      setTooltipTarget(null)
    }
  }, [itemsLimit, setTooltipTarget])

  const showPlaceholder = value.length === 0

  let valueText // Used to display which stations/groups have been selected

  // Selector text for stations
  let selectedStationsText = value
    .filter(item => item.includes('stat-'))
    .slice()
    .sort((a, b) => {
      const ai = stations.findIndex(s => s.id === a)
      const bi = stations.findIndex(s => s.id === b)
      return ai - bi
    })
    .map(id => {
      const station = stations.find(s => s.id === id)
      return station ? station.name : id
    })
    .join(', ')
  
  // Selector text for groups
  let selectedGroupsText = undefined
  
  // Ensure we have groups data before creating placeholder text
  if(groups && groups.length > 0){
    selectedGroupsText = value
      .filter(item => !item.includes('stat-'))
      .map(id => {
        return getGroupData(groups, id).name
      })
      .join(', ')
  }

  // Selector text for stations and groups
  const selectedStationsAndGroupsText = [selectedStationsText, selectedGroupsText].filter(Boolean).join(', ')

  valueText = allowGroups ? selectedStationsAndGroupsText : selectedStationsText
  
  const displayedValue = showPlaceholder ? 
    placeholder : 
    value.length === stations.length ? 
      allStationsText ?? valueText :
      valueText

  const handleDropdownClick = () => {
    if (!disabled) {
      !open &&
        amplitudeEvent(
          UserEvents.PerformanceReport.STATIONS_FILTER_SELECTED(
            getFirstPartOfURLPath(window.location.pathname)
          )
        )
      setOpen(!open)
    }
  }

  const handleItemClick = event => {
    event.stopPropagation()
  }

  const handleSelectAll = () => {
    const value = stations.map(s => s.id)
    onValueChange(value)
    setSearch('')
  }

  const handleDeselectAll = () => {
    onValueChange([])
    setSearch('')
  }

  const handleSearchChange = event => {
    const search = event.target.value.replace(/\s+/g, ' ')
    setSearch(search)
  }

  const handleTooltipClick = () => {
    setTooltipTarget(null)
  }

  const handleDropdownItemClick = (event, stationOrGroupId) => {
    let newValue = [...value]

    if (newValue.includes(stationOrGroupId)) {
      newValue = newValue.filter((id) => id !== stationOrGroupId)
    } else {
      newValue.push(stationOrGroupId)
    }

    const maxItemsSelected = itemsLimit - groupByVar()['PERIOD_ANALYSIS'].length
    if (!itemsLimit || newValue.length <= maxItemsSelected) {
      setTooltipTarget(null)
      onValueChange([...newValue])
    } else {
      setTooltipTarget(event.target)
    }
  }

  return (
    <div
      className={cx('dropdown', customClass, {
        disabled,
        open,
      })}
      onClick={handleDropdownClick}
      data-testid="station-select"
    >
      <span className={cx({ placeholder: showPlaceholder })}>
        {leftAccessory} {displayedValue}
      </span>
      {!disabled && <DropdownIcon className={cx('dropdown-icon')} />}
      {open && (
        <div className={cx('items')} onClick={handleItemClick}>
          <div className={cx('item')} onClick={handleDropdownClick}>
            <span className={cx({ placeholder: showPlaceholder })}>
              {leftAccessory} {displayedValue}
            </span>
            <DropdownIcon className={cx('dropdown-icon')} />
          </div>
          {!(showGroups && groupsLoading) && <div className={cx('search')}>
            <input
              type="text"
              value={search}
              onChange={handleSearchChange}
              placeholder={`Search for a ${!showGroups ? 'station' : 'group'}`}
              data-testid="search"
            />
            <SearchIcon />
          </div>}
          <div>
            {(allowGroups ? !showGroups : true) ? (
              <div
                className={cx('items-list')}
                data-testid="items-list"
                style={{ maxHeight }}
              >
                <StationList
                  stations={stations}
                  value={value}
                  search={search}
                  handleDropdownItemClick={handleDropdownItemClick}
                />
              </div>
            ) : (
              <div>
                <GroupList
                  groups={groups}
                  value={value}
                  search={search}
                  handleDropdownItemClick={handleDropdownItemClick}
                />
              </div>
            )}
            <Overlay target={tooltipTarget} show={!!tooltipTarget} position="top">
              <Tooltip className={cx('tooltip')} onClick={handleTooltipClick}>
                <div className={cx('tooltip-content')} data-testid="tooltip"> 
                  {t('widgets.stationSelector.itemsLimit', {
                    limit: itemsLimit
                  })}
                </div>
              </Tooltip>
            </Overlay>
          </div>
          {selectButtons && (
            <div className={cx('selection-helpers')}>
              <button
                onClick={handleSelectAll}
                className={cx('select-all')}
                data-testid="select-all"
              >
                {t('widgets.stationSelector.selectAll')}
              </button>
              <button
                onClick={handleDeselectAll}
                className={cx('deselect-all')}
                data-testid="deselect-all"
              >
                {t('widgets.stationSelector.deselectAll')}
              </button>
            </div>
          )}
          {allowGroups && (
            <div className={cx('button-wrapper')}>
              <button className={cx('button', !showGroups ? 'open' : '')} onClick={() => setShowGroups(false)}>Stations {calculateSelectedStationsAndGroupsAmount('stations', value) > 0 ? '(' + calculateSelectedStationsAndGroupsAmount('stations', value) + ')' : ''}</button>
              <button data-testid="station-select-groups-btn" className={cx('button', showGroups ? 'open' : '')} onClick={() => setShowGroups(true)}>Groups {calculateSelectedStationsAndGroupsAmount('groups', value) > 0 ? '(' + calculateSelectedStationsAndGroupsAmount('groups', value) + ')' : ''}</button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

StationSelector.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string),
  onValueChange: PropTypes.func,
  customClass: PropTypes.string,
  placeholder: PropTypes.string,
  maxHeight: PropTypes.string,
  allStationsText: PropTypes.string,
  disabled: PropTypes.bool,
  leftAccessory: PropTypes.node,
  removeButtons: PropTypes.bool,
  itemsLimit: PropTypes.number,
  allowGroups: PropTypes.bool,
}

StationSelector.defaultProps = {
  value: [],
  onValueChange: null,
  customClass: null,
  placeholder: null,
  maxHeight: null,
  allStationsText: null,
  disabled: false,
  leftAccessory: null,
  selectButtons: true,
  itemsLimit: null,
  allowGroups: false
}

export { StationSelector }
